import React from 'react';

/* ─────────────────────────────────────────────────────────────
   PRASI LABS — Pharma-themed decorative SVG animations
   All pure SVG/CSS — no external deps, GPU-accelerated
───────────────────────────────────────────────────────────── */

const CSS = `
  .ph-spin-slow  { animation: ph-spin-slow 14s linear infinite; transform-box: fill-box; transform-origin: center; }
  .ph-spin-med   { animation: ph-spin-slow  8s linear infinite; transform-box: fill-box; transform-origin: center; }
  .ph-spin-rev   { animation: ph-spin-rev  12s linear infinite; transform-box: fill-box; transform-origin: center; }
  .ph-wobble     { animation: ph-wobble 3.5s ease-in-out infinite; transform-box: fill-box; transform-origin: bottom center; }
  .ph-float      { animation: ph-float 6s ease-in-out infinite; }
  .ph-breathe    { animation: ph-breathe 4s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
  @keyframes ph-spin-slow { from { transform: rotate(0deg);  } to { transform: rotate(360deg);  } }
  @keyframes ph-spin-rev  { from { transform: rotate(0deg);  } to { transform: rotate(-360deg); } }
  @keyframes ph-wobble    { 0%,100% { transform: rotate(-2.5deg) translateX(0);   }
                             50%    { transform: rotate( 2.5deg) translateX(1px);  } }
  @keyframes ph-float     { 0%,100% { transform: translateY(0);   }
                             50%    { transform: translateY(-9px); } }
  @keyframes ph-breathe   { 0%,100% { transform: scale(1);    }
                             50%    { transform: scale(1.07);  } }
`;

let _flaskCounter = 0;

/* ══════════════════════════════════════════════════════════════
   1. OrbitingAtom — Bohr model with 3 tilted electron orbits
══════════════════════════════════════════════════════════════ */
export function OrbitingAtom({ size = 130, color = '#1d4ed8', style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={style} aria-hidden="true">
      <style>{CSS}</style>

      {/* Outer ambient glow */}
      <circle cx="50" cy="50" r="49" fill={color} opacity="0.04" className="ph-breathe"/>

      {/* Nucleus layers */}
      <circle cx="50" cy="50" r="13" fill={color} opacity="0.18"/>
      <circle cx="50" cy="50" r="8.5" fill={color}/>
      <circle cx="46" cy="46.5" r="2.2" fill="white" opacity="0.75"/>
      {/* Nucleus pulse */}
      <circle cx="50" cy="50" r="8.5" fill={color} opacity="0.5">
        <animate attributeName="r"       values="8.5;11;8.5"   dur="2.4s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.5;0;0.5"    dur="2.4s" repeatCount="indefinite"/>
      </circle>

      {/* ── Orbit 1: horizontal ───────────────────────── */}
      <ellipse cx="50" cy="50" rx="41" ry="14" fill="none" stroke={color} strokeWidth="0.8" opacity="0.28"/>
      <g>
        <animateTransform attributeName="transform" type="rotate"
          from="0 50 50" to="360 50 50" dur="3s" repeatCount="indefinite"/>
        <circle cx="91" cy="50" r="4.5" fill="#60a5fa"/>
        <circle cx="88.5" cy="47.5" r="1.4" fill="white" opacity="0.8"/>
      </g>

      {/* ── Orbit 2: tilted +60° ─────────────────────── */}
      <g transform="rotate(60, 50, 50)">
        <ellipse cx="50" cy="50" rx="41" ry="14" fill="none" stroke={color} strokeWidth="0.8" opacity="0.28"/>
        <g>
          <animateTransform attributeName="transform" type="rotate"
            from="360 50 50" to="0 50 50" dur="4.6s" repeatCount="indefinite"/>
          <circle cx="91" cy="50" r="4.5" fill="#34d399"/>
          <circle cx="88.5" cy="47.5" r="1.4" fill="white" opacity="0.8"/>
        </g>
      </g>

      {/* ── Orbit 3: tilted -60° ─────────────────────── */}
      <g transform="rotate(-60, 50, 50)">
        <ellipse cx="50" cy="50" rx="41" ry="14" fill="none" stroke={color} strokeWidth="0.8" opacity="0.28"/>
        <g>
          <animateTransform attributeName="transform" type="rotate"
            from="0 50 50" to="360 50 50" dur="6.2s" repeatCount="indefinite"/>
          <circle cx="91" cy="50" r="4.5" fill="#f59e0b"/>
          <circle cx="88.5" cy="47.5" r="1.4" fill="white" opacity="0.8"/>
        </g>
      </g>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════
   2. BubblingFlask — Erlenmeyer flask with rising bubbles
══════════════════════════════════════════════════════════════ */
export function BubblingFlask({ size = 110, color = '#0891b2', style }) {
  const uid = React.useMemo(() => `fl${++_flaskCounter}`, []);

  return (
    <svg width={size} height={size} viewBox="0 0 80 96" style={style} aria-hidden="true">
      <style>{CSS}</style>
      <defs>
        <linearGradient id={`${uid}-liq`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.75"/>
          <stop offset="100%" stopColor="#0a1628" stopOpacity="0.96"/>
        </linearGradient>
        <radialGradient id={`${uid}-glow`} cx="50%" cy="80%" r="60%">
          <stop offset="0%" stopColor={color} stopOpacity="0.35"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </radialGradient>
        <clipPath id={`${uid}-clip`}>
          <path d="M 28,33 L 14,63 Q 5,78 5,86 Q 5,96 40,96 Q 75,96 75,86 Q 75,78 66,63 L 52,33 Z"/>
        </clipPath>
      </defs>

      {/* Bottom glow */}
      <ellipse cx="40" cy="90" rx="32" ry="8" fill={`url(#${uid}-glow)`}/>

      {/* Flask group with wobble */}
      <g className="ph-wobble">
        {/* Neck */}
        <rect x="28" y="6" width="24" height="28" rx="4"
          fill="rgba(255,255,255,0.06)" stroke={color} strokeWidth="2"/>
        {/* Stopper */}
        <rect x="24" y="2" width="32" height="9" rx="4.5" fill={color} opacity="0.75"/>
        <rect x="29" y="4" width="22" height="5" rx="2.5" fill="white" opacity="0.25"/>

        {/* Flask body outline */}
        <path d="M 28,33 L 14,63 Q 5,78 5,86 Q 5,96 40,96 Q 75,96 75,86 Q 75,78 66,63 L 52,33 Z"
          fill="rgba(255,255,255,0.04)" stroke={color} strokeWidth="2.2" strokeLinejoin="round"/>

        {/* Liquid body */}
        <rect x="5" y="64" width="70" height="32" fill={`url(#${uid}-liq)`}
          clipPath={`url(#${uid}-clip)`}/>

        {/* Liquid surface shimmer */}
        <ellipse cx="40" cy="64" rx="25" ry="3" fill={color} opacity="0.4"
          clipPath={`url(#${uid}-clip)`}>
          <animate attributeName="ry" values="3;4.5;3" dur="2s" repeatCount="indefinite"/>
          <animate attributeName="cy"  values="64;62;64" dur="2s" repeatCount="indefinite"/>
        </ellipse>

        {/* Bubbles — each has cy, dur, delay */}
        {[
          [27, '2s',   '0s'],
          [40, '2.4s', '0.8s'],
          [55, '1.9s', '1.4s'],
          [34, '2.7s', '0.4s'],
          [48, '2.1s', '1.9s'],
        ].map(([cx, dur, delay], i) => (
          <circle key={i} cx={cx} cy="88" r="3" fill="none"
            stroke="rgba(255,255,255,0.65)" strokeWidth="1"
            clipPath={`url(#${uid}-clip)`}>
            <animate attributeName="cy"      values="90;50"        dur={dur} begin={delay} repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0;0.9;0.7;0"  dur={dur} begin={delay} repeatCount="indefinite"/>
            <animate attributeName="r"       values="2;4.5;3.5;2"  dur={dur} begin={delay} repeatCount="indefinite"/>
          </circle>
        ))}

        {/* Glass highlight */}
        <path d="M 33,38 L 20,62 Q 13,74 11,83"
          fill="none" stroke="white" strokeWidth="1.8" strokeOpacity="0.22" strokeLinecap="round"/>
        <path d="M 40,10 L 40,28"
          fill="none" stroke="white" strokeWidth="1.8" strokeOpacity="0.22" strokeLinecap="round"/>

        {/* Flask rim */}
        <rect x="28" y="31" width="24" height="5" rx="2" fill={color} opacity="0.4"/>
      </g>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════
   3. MoleculeNetwork — pulsing nodes + shimmering bond graph
══════════════════════════════════════════════════════════════ */
export function MoleculeNetwork({ size = 160, style }) {
  const nodes = [
    { x: 80,  y: 30,  r: 8,   color: '#1d4ed8', delay: '0s'   },
    { x: 138, y: 68,  r: 6.5, color: '#0891b2', delay: '0.5s'  },
    { x: 125, y: 128, r: 9,   color: '#059669', delay: '1s'    },
    { x: 65,  y: 150, r: 6,   color: '#1d4ed8', delay: '1.5s'  },
    { x: 22,  y: 110, r: 7.5, color: '#7c3aed', delay: '0.7s'  },
    { x: 28,  y: 52,  r: 6,   color: '#0891b2', delay: '1.2s'  },
    { x: 80,  y: 95,  r: 5.5, color: '#f59e0b', delay: '0.3s'  }, // center hub
  ];
  const edges = [
    [0,1],[1,2],[2,3],[3,4],[4,5],[5,0], // outer ring
    [0,6],[1,6],[2,6],[4,6],[5,6],       // spokes to hub
  ];
  return (
    <svg width={size} height={size} viewBox="0 0 160 180" style={style} aria-hidden="true">
      <style>{CSS}</style>

      {/* Edges */}
      {edges.map(([a, b], i) => {
        const na = nodes[a], nb = nodes[b];
        const len = Math.hypot(nb.x - na.x, nb.y - na.y);
        return (
          <line key={i}
            x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
            stroke="#1d4ed8" strokeWidth="1.8"
            strokeDasharray={`${len*0.4} ${len*0.6}`}>
            <animate attributeName="stroke-opacity" values="0.15;0.6;0.15"
              dur={`${2.2 + i * 0.28}s`} repeatCount="indefinite"/>
            <animate attributeName="stroke-dashoffset" values="0;-30;0"
              dur={`${3 + i * 0.3}s`} repeatCount="indefinite"/>
          </line>
        );
      })}

      {/* Nodes */}
      {nodes.map((n, i) => (
        <g key={i}>
          {/* Outer glow ring */}
          <circle cx={n.x} cy={n.y} r={n.r + 5} fill={n.color} opacity="0.1">
            <animate attributeName="r"       values={`${n.r+4};${n.r+10};${n.r+4}`} dur={`${2.5+i*0.4}s`} begin={n.delay} repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.12;0.03;0.12"                dur={`${2.5+i*0.4}s`} begin={n.delay} repeatCount="indefinite"/>
          </circle>
          {/* Main atom */}
          <circle cx={n.x} cy={n.y} r={n.r} fill={n.color}>
            <animate attributeName="r" values={`${n.r};${n.r*1.18};${n.r}`} dur={`${2.5+i*0.4}s`} begin={n.delay} repeatCount="indefinite"/>
          </circle>
          {/* Shine highlight */}
          <circle cx={n.x - n.r*0.34} cy={n.y - n.r*0.34} r={n.r*0.28} fill="white" opacity="0.72"/>
        </g>
      ))}
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════
   4. SpinningBenzene — hexagonal ring with delocalized bonds
══════════════════════════════════════════════════════════════ */
export function SpinningBenzene({ size = 100, color = '#1d4ed8', style }) {
  const cx = 50, cy = 50, R = 34, r = 21, ar = 5;

  const verts = Array.from({ length: 6 }, (_, i) => {
    const a = (i * 60) * (Math.PI / 180);
    return { x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) };
  });
  const poly = verts.map(v => `${v.x.toFixed(2)},${v.y.toFixed(2)}`).join(' ');

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={style} aria-hidden="true">
      <style>{CSS}</style>

      {/* Outer ambient glow */}
      <circle cx={cx} cy={cy} r="47" fill={color} opacity="0.05" className="ph-breathe"/>

      {/* Spinning group */}
      <g className="ph-spin-slow">
        {/* Bond hexagon */}
        <polygon points={poly} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round"/>

        {/* Inner delocalized ring */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="1.6"
          strokeDasharray="8 4" opacity="0.65">
          <animate attributeName="stroke-dashoffset" values="0;-36;0" dur="4s" repeatCount="indefinite"/>
        </circle>

        {/* Atom circles at each vertex */}
        {verts.map((v, i) => (
          <g key={i}>
            <circle cx={v.x} cy={v.y} r={ar + 2.5} fill={color} opacity="0.14"/>
            <circle cx={v.x} cy={v.y} r={ar} fill={color}/>
            <circle cx={v.x - ar*0.33} cy={v.y - ar*0.33} r={ar*0.3} fill="white" opacity="0.78"/>
          </g>
        ))}

        {/* Center dot */}
        <circle cx={cx} cy={cy} r="3.5" fill={color} opacity="0.45"/>
        <circle cx={cx - 1.5} cy={cy - 1.5} r="1" fill="white" opacity="0.7"/>
      </g>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════
   5. DNAStrand — double helix with infinite scroll
══════════════════════════════════════════════════════════════ */
export function DNAStrand({ size = 70, height = 160, style }) {
  const period = 80;          // px per full helix turn
  const amp    = 19;           // horizontal amplitude
  const cxVal  = 35;           // center x
  const reps   = 2;            // draw 2 repeats for seamless loop
  const step   = 3;            // path resolution

  function strandPath(phase) {
    const pts = [];
    for (let y = 0; y <= period * reps; y += step) {
      const x = cxVal + amp * Math.sin((y / period) * Math.PI * 2 + phase);
      pts.push(`${pts.length === 0 ? 'M' : 'L'} ${x.toFixed(1)},${y}`);
    }
    return pts.join(' ');
  }

  // Rungs every quarter-period
  const rungs = [];
  for (let i = 0; i < reps * 4; i++) {
    const y  = (i / 4) * period;
    const a  = (y / period) * Math.PI * 2;
    const x1 = (cxVal + amp * Math.sin(a)).toFixed(1);
    const x2 = (cxVal + amp * Math.sin(a + Math.PI)).toFixed(1);
    rungs.push({ x1, x2, y, front: Math.cos(a) >= 0 });
  }

  const path1 = strandPath(0);
  const path2 = strandPath(Math.PI);

  return (
    <svg
      width={size}
      height={height}
      viewBox={`0 0 ${cxVal * 2} ${period}`}
      style={{ overflow: 'hidden', ...style }}
      aria-hidden="true"
    >
      <style>{CSS}</style>
      <g>
        <animateTransform
          attributeName="transform" type="translate"
          values={`0,0; 0,-${period}`}
          dur="3.8s" repeatCount="indefinite" calcMode="linear"
        />

        {/* Back rungs (behind strands) */}
        {rungs.filter(r => !r.front).map((r, i) => (
          <line key={i} x1={r.x1} y1={r.y} x2={r.x2} y2={r.y}
            stroke="#a7f3d0" strokeWidth="1.6" strokeOpacity="0.45" strokeLinecap="round"/>
        ))}

        {/* Strand 1 — blue */}
        <path d={path1} fill="none" stroke="#1d4ed8" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
        {/* Strand 2 — green */}
        <path d={path2} fill="none" stroke="#059669" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>

        {/* Front rungs */}
        {rungs.filter(r => r.front).map((r, i) => (
          <line key={i} x1={r.x1} y1={r.y} x2={r.x2} y2={r.y}
            stroke="#60a5fa" strokeWidth="2.2" strokeOpacity="0.85" strokeLinecap="round"/>
        ))}

        {/* Atom dots at rung endpoints */}
        {rungs.map((r, i) => (
          <g key={i} opacity={r.front ? 1 : 0.5}>
            <circle cx={r.x1} cy={r.y} r="3.8" fill="#1d4ed8"/>
            <circle cx={r.x1} cy={r.y} r="1.2" fill="white" opacity="0.7"
              transform={`translate(-1.2,-1.2)`}/>
            <circle cx={r.x2} cy={r.y} r="3.8" fill="#059669"/>
            <circle cx={r.x2} cy={r.y} r="1.2" fill="white" opacity="0.7"
              transform={`translate(-1.2,-1.2)`}/>
          </g>
        ))}
      </g>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════
   6. MorphingMolecule — slowly rotating molecule that breathes
══════════════════════════════════════════════════════════════ */
export function MorphingMolecule({ size = 140, style }) {
  const atoms = [
    { x: 70, y: 26,  r: 8,   color: '#1d4ed8', delay: '0s'   },
    { x: 112,y: 55,  r: 7,   color: '#0891b2', delay: '0.6s'  },
    { x: 108,y: 102, r: 9,   color: '#059669', delay: '1.1s'  },
    { x: 68, y: 122, r: 6.5, color: '#1d4ed8', delay: '1.6s'  },
    { x: 28, y: 100, r: 7.5, color: '#7c3aed', delay: '0.9s'  },
    { x: 28, y: 53,  r: 6.5, color: '#0891b2', delay: '0.4s'  },
    { x: 70, y: 74,  r: 5.5, color: '#f59e0b', delay: '0.2s'  }, // hub
  ];
  const edges = [
    [0,1],[1,2],[2,3],[3,4],[4,5],[5,0],
    [0,6],[2,6],[4,6],
  ];

  return (
    <svg width={size} height={size} viewBox="0 0 140 148" style={style} aria-hidden="true">
      <style>{CSS}</style>

      {/* Whole molecule slowly rotates + breathes */}
      <g className="ph-spin-slow" style={{ transformOrigin: '70px 74px' }}>

        {/* Bond lines */}
        {edges.map(([a, b], i) => {
          const na = atoms[a], nb = atoms[b];
          return (
            <line key={i}
              x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
              stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round">
              <animate attributeName="stroke-opacity"
                values="0.25;0.55;0.25"
                dur={`${3 + i * 0.45}s`} repeatCount="indefinite"/>
            </line>
          );
        })}

        {/* Atom circles */}
        {atoms.map((a, i) => (
          <g key={i}>
            {/* Glow */}
            <circle cx={a.x} cy={a.y} r={a.r + 4} fill={a.color} opacity="0.12">
              <animate attributeName="r"       values={`${a.r+3};${a.r+8};${a.r+3}`} dur={`${2.8+i*0.5}s`} begin={a.delay} repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.14;0.04;0.14"                dur={`${2.8+i*0.5}s`} begin={a.delay} repeatCount="indefinite"/>
            </circle>
            {/* Body */}
            <circle cx={a.x} cy={a.y} r={a.r} fill={a.color}>
              <animate attributeName="r" values={`${a.r};${a.r*1.2};${a.r}`} dur={`${2.8+i*0.5}s`} begin={a.delay} repeatCount="indefinite"/>
            </circle>
            {/* Shine */}
            <circle cx={a.x - a.r*0.35} cy={a.y - a.r*0.35} r={a.r*0.28} fill="white" opacity="0.72"/>
          </g>
        ))}
      </g>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════
   7. FloatingParticles — ambient background particle field
══════════════════════════════════════════════════════════════ */
export function FloatingParticles({ width = 400, height = 300, count = 18, color = '#1d4ed8', style }) {
  const particles = React.useMemo(() => (
    Array.from({ length: count }, (_, i) => ({
      x:     5 + (i * 371) % 90,
      y:     5 + (i * 173) % 90,
      r:     1.5 + (i % 4) * 0.8,
      dur:   `${4 + (i % 6) * 1.2}s`,
      delay: `${(i % 8) * 0.6}s`,
      dy:    8 + (i % 5) * 4,
    }))
  ), [count]);

  return (
    <svg width={width} height={height} viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid slice"
      style={{ ...style }} aria-hidden="true">
      <style>{CSS}</style>
      {particles.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={p.r} fill={color} opacity="0.35">
          <animate attributeName="cy"      values={`${p.y};${p.y - p.dy};${p.y}`} dur={p.dur} begin={p.delay} repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.15;0.55;0.15"                 dur={p.dur} begin={p.delay} repeatCount="indefinite"/>
          <animate attributeName="r"       values={`${p.r};${p.r*1.5};${p.r}`}    dur={p.dur} begin={p.delay} repeatCount="indefinite"/>
        </circle>
      ))}
    </svg>
  );
}
